/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import cx from 'classnames';

import { IconButton } from '@coursera/cds-core';
import type { IconButtonProps } from '@coursera/cds-core';

export type Props = {
  selected?: boolean;
} & IconButtonProps;

const styles = {
  coachIconButton: css`
    /* CDS overrides */
    --cds-border-radius-50: 50%;
    --cds-color-neutral-disabled: var(--cds-color-grey-100);
    --cds-color-interactive-primary-selected: var(--cds-color-blue-600);
    --cds-color-interactive-background-primary-hover-weak: var(--cds-color-emphasis-primary-background-xweak);
    --cds-color-interactive-background-primary-pressed-weak: var(--cds-color-emphasis-primary-background-xweak);
    --cds-color-interactive-stroke-primary-focus: var(--cds-color-emphasis-primary-background-xxstrong);
    --cds-color-interactive-stroke-primary-focus-invert: var(--cds-color-emphasis-primary-background-weak);

    box-shadow: none;

    &.selected {
      svg path {
        fill: var(--cds-color-interactive-primary-selected);
      }

      background: var(--cds-color-emphasis-primary-background-xweak);
    }

    &[aria-disabled='true'] {
      background-color: transparent;

      svg path {
        fill: var(--cds-color-neutral-disabled);
      }
    }

    /* increases target area for the tooltip trigger, see [COACH-1007] */
    svg {
      padding: var(--cds-spacing-100);
      box-sizing: content-box; /* prevents icon shrinking */
      display: inline-block; /* prevents icon shrinking */
    }
  `,
};

/**
 * Simple wrapper around CDS IconButton to provide Coach-specific styles.
 */
const CoachIconButton = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const { size = 'medium', selected = false, disabled, ...rest } = props;

  // use ariaDisabled when button is disabled but there is a tooltip
  // this allows users to focus/hover on the button to see the tooltip
  // while still keeping the underlying disabled styling for CDS IconButton
  const shouldAriaDisableOnly = disabled && !!rest.tooltip;

  return (
    <IconButton
      ref={ref}
      size={size}
      variant="ghost"
      css={styles.coachIconButton}
      className={cx({ selected })}
      {...rest}
      aria-disabled={shouldAriaDisableOnly}
      disabled={!shouldAriaDisableOnly && disabled}
    />
  );
});

export default CoachIconButton;
